import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportIssue, getFriends, getPendingFriendPosts } from '../api';
import logoImg from '../photos/Gemini_Generated_Image_bqlpj5bqlpj5bqlp-Photoroom.png';

// ── Logo ──────────────────────────────────────────────────────────────────────
// sm = 28px (navbar/footer), md = 40px (default), lg = 80px (hero centrepiece)
export function Logo({ size = 'md', className = '' }) {
  const px = size === 'sm' ? 'w-7 h-7' : size === 'lg' ? 'w-20 h-20' : 'w-10 h-10';
  return (
    <img
      src={logoImg}
      alt="GolfBoard logo"
      className={`${px} object-contain ${className}`}
    />
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md' }) {
  const sz = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
  return (
    <div className={`${sz} border-2 border-green-200 border-t-green-700 rounded-full animate-spin`} />
  );
}

// ── LoadingPage ───────────────────────────────────────────────────────────────
export function LoadingPage() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-green-800 font-medium text-sm tracking-wide">Loading...</p>
      </div>
    </div>
  );
}

// ── Messages ──────────────────────────────────────────────────────────────────
export function ErrorMsg({ message }) {
  if (!message) return null;
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
      {message}
    </div>
  );
}

export function SuccessMsg({ message }) {
  if (!message) return null;
  return (
    <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3">
      {message}
    </div>
  );
}

// ── RankBadge ─────────────────────────────────────────────────────────────────
export function RankBadge({ rank }) {
  let cls = 'rank-other';
  if (rank === 1) cls = 'rank-1';
  else if (rank === 2) cls = 'rank-2';
  else if (rank === 3) cls = 'rank-3';
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${cls}`}>
      {rank}
    </span>
  );
}

// ── ScoreBadge ────────────────────────────────────────────────────────────────
export function ScoreBadge({ overUnder }) {
  if (overUnder === 0) return <span className="text-gray-600 font-semibold">E</span>;
  if (overUnder < 0) return <span className="text-green-700 font-semibold">{overUnder}</span>;
  return <span className="text-red-600 font-semibold">+{overUnder}</span>;
}

// ── Avatar ────────────────────────────────────────────────────────────────────
export function Avatar({ url, username, size = 'md' }) {
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-16 h-16 text-xl' : 'w-10 h-10 text-sm';
  if (url) {
    return <img src={url} alt={username} className={`${sz} rounded-full object-cover border-2 border-amber-200`} />;
  }
  const initials = username ? username.slice(0, 2).toUpperCase() : '?';
  return (
    <div className={`${sz} rounded-full bg-green-800 text-amber-100 flex items-center justify-center font-semibold border-2 border-amber-200`}>
      {initials}
    </div>
  );
}

// ── VerifiedBadge ─────────────────────────────────────────────────────────────
// Shown next to scores that have been vouched for by another player.
// Clickable on the leaderboard to show who verified the score.
export function VerifiedBadge({ small = false }) {
  return (
    <span
      title="This score has been verified by another player"
      className={`inline-flex items-center gap-1 bg-green-100 text-green-800 font-semibold rounded-full ${
        small ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-0.5'
      }`}
    >
      ✓ Verified
    </span>
  );
}

// ── AdPlaceholder ─────────────────────────────────────────────────────────────
// Visible placeholder marking where Google AdSense units will go in the future.
// Replace the inner content with the actual AdSense script when ready.
export function AdPlaceholder({ label = 'Advertisement', className = '' }) {
  return (
    <div className={`w-full border-2 border-dashed border-amber-200 rounded-xl bg-amber-50/60 flex flex-col items-center justify-center py-6 px-4 text-center ${className}`}>
      <span className="text-2xl mb-1">📢</span>
      <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest">{label}</p>
      <p className="text-xs text-amber-600 mt-0.5">Ad space — Google AdSense (coming soon)</p>
    </div>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────
export function Navbar() {
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);

  // Poll for pending friend requests + pending friend-posted scores
  useEffect(() => {
    if (!user) return;
    const load = () => {
      Promise.all([getFriends(), getPendingFriendPosts()])
        .then(([friendsData, postsData]) => {
          const friendReqs = friendsData?.pending_received?.length || 0;
          const pendingPosts = postsData?.length || 0;
          setPendingRequests(friendReqs + pendingPosts);
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/login');
  };

  const closeMenu = () => setMenuOpen(false);

  const navLink = (to, label) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        className={`text-sm font-medium transition-colors ${
          active ? 'text-amber-600' : 'text-green-900 hover:text-amber-600'
        }`}
      >
        {label}
      </Link>
    );
  };

  const mobileNavLink = (to, label, icon) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        onClick={closeMenu}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
          active
            ? 'bg-amber-50 text-amber-700'
            : 'text-green-900 hover:bg-green-50'
        }`}
      >
        <span className="text-base">{icon}</span>
        {label}
      </Link>
    );
  };

  return (
    <>
      <nav className="bg-white border-b border-amber-100 shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">

          {/* Logo */}
          <Link to={user ? '/home' : '/'} className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-bold text-green-900 text-lg hidden sm:block">GolfBoard Scores</span>
          </Link>

          {/* Desktop nav — hidden on mobile */}
          <div className="hidden sm:flex items-center gap-5">
            {user && navLink('/home', 'Leaderboards')}
            {user && navLink('/local-legends', '🏅 Local Legends')}
            {user && navLink('/people', 'People')}
            {user && (
              <Link to={`/profile/${user.id}`} className="relative flex items-center gap-2 hover:opacity-80 transition-opacity">
                <Avatar url={profile?.avatar_url} username={profile?.username} size="sm" />
                <span className="text-sm font-medium text-green-900">{profile?.username || 'Profile'}</span>
                {pendingRequests > 0 && (
                  <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                    {pendingRequests}
                  </span>
                )}
              </Link>
            )}
            {user ? (
              <button onClick={handleLogout} className="text-sm text-gray-400 hover:text-red-500 transition-colors">
                Sign out
              </button>
            ) : (
              <Link to="/login" className="btn-primary text-sm">Sign In</Link>
            )}
          </div>

          {/* Mobile right side — avatar + hamburger */}
          <div className="flex sm:hidden items-center gap-3">
            {user && (
              <Link to={`/profile/${user.id}`} onClick={closeMenu} className="relative hover:opacity-80 transition-opacity">
                <Avatar url={profile?.avatar_url} username={profile?.username} size="sm" />
                {pendingRequests > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                    {pendingRequests}
                  </span>
                )}
              </Link>
            )}
            {!user && (
              <Link to="/login" className="btn-primary text-sm">Sign In</Link>
            )}
            {user && (
              <button
                onClick={() => setMenuOpen(o => !o)}
                className="p-2 rounded-lg text-green-900 hover:bg-green-50 transition-colors"
                aria-label="Toggle menu"
              >
                {menuOpen ? (
                  // X icon
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="4" y1="4" x2="16" y2="16" />
                    <line x1="16" y1="4" x2="4" y2="16" />
                  </svg>
                ) : (
                  // Hamburger icon
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="6" x2="17" y2="6" />
                    <line x1="3" y1="10" x2="17" y2="10" />
                    <line x1="3" y1="14" x2="17" y2="14" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {menuOpen && user && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 sm:hidden"
            onClick={closeMenu}
          />
          {/* Menu panel */}
          <div className="fixed top-14 left-0 right-0 z-40 bg-white border-b border-amber-100 shadow-lg sm:hidden fade-in">
            <div className="px-3 py-3 space-y-1">
              {/* Profile row at top */}
              <Link
                to={`/profile/${user.id}`}
                onClick={closeMenu}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-green-50 transition-colors mb-1"
              >
                <Avatar url={profile?.avatar_url} username={profile?.username} size="sm" />
                <div>
                  <div className="text-sm font-semibold text-green-900">{profile?.username || 'Profile'}</div>
                  <div className="text-xs text-gray-400">View my profile</div>
                </div>
              </Link>

              <div className="h-px bg-amber-100 mx-2 mb-1" />

              {mobileNavLink('/home', 'Leaderboards', '🏆')}
              {mobileNavLink('/local-legends', 'Local Legends', '🏅')}
              {mobileNavLink('/people', 'Find Golfers', '👤')}

              <div className="h-px bg-amber-100 mx-2 my-1" />

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
              >
                <span className="text-base">🚪</span>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── PageHeader ────────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, back, children }) {
  const navigate = useNavigate();
  return (
    <div className="mb-6">
      {back && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-green-700 hover:text-green-900 mb-3 transition-colors"
        >
          <span>←</span> Back
        </button>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-green-900">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon = '🏌️', title, message, action }) {
  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-3">{icon}</div>
      <h3 className="font-serif font-semibold text-green-900 text-lg mb-1">{title}</h3>
      <p className="text-gray-500 text-sm mb-4">{message}</p>
      {action}
    </div>
  );
}

// ── formatDate ────────────────────────────────────────────────────────────────
export function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

// ── Report Issue Modal ────────────────────────────────────────────────────────
const MAX_CHARS = 300;

function ReportIssueModal({ onClose }) {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const remaining = MAX_CHARS - description.length;
  const isNearLimit = remaining <= 50;
  const isOverLimit = remaining < 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isOverLimit) return;
    setError('');
    setLoading(true);
    try {
      await reportIssue(description.trim());
      setSuccess("Thanks for the report! We'll look into it.");
      setTimeout(() => onClose(), 2200);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div className="card w-full max-w-sm p-6 fade-in">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-serif font-bold text-xl text-green-900">Report an Issue</h3>
            <p className="text-xs text-gray-400 mt-0.5">Describe the problem in 300 characters or less</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none ml-3">✕</button>
        </div>

        {success ? (
          <div className="py-4 text-center">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-green-800 font-medium text-sm">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <textarea
                className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 bg-white transition-colors resize-none min-h-[120px] ${
                  isOverLimit
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-400'
                    : 'border-gray-200 focus:border-green-600 focus:ring-green-600'
                }`}
                placeholder="Describe what went wrong..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                autoFocus
              />
              <span className={`absolute bottom-2.5 right-3 text-xs ${
                isOverLimit ? 'text-red-500 font-semibold' : isNearLimit ? 'text-amber-600' : 'text-gray-400'
              }`}>{remaining}</span>
            </div>
            <ErrorMsg message={error} />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary flex-1" disabled={loading}>Cancel</button>
              <button
                type="submit"
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={loading || isOverLimit || description.trim().length < 10}
              >
                {loading ? <Spinner size="sm" /> : 'Send Report'}
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center">Min 10 characters · Max 30 reports per account</p>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
export function Footer() {
  const { user } = useAuth();
  const [showReport, setShowReport] = useState(false);

  return (
    <>
      <footer className="bg-green-900 text-green-300 py-6 px-5 mt-auto">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Logo size="sm" />
              <span className="font-serif font-semibold text-amber-200">GolfBoard Scores</span>
              <span className="text-green-500 text-xs ml-1">© {new Date().getFullYear()}</span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs">
              <Link to="/home" className="hover:text-amber-200 transition-colors">Leaderboards</Link>
              <Link to="/local-legends" className="hover:text-amber-200 transition-colors">Local Legends</Link>
              <Link to="/people" className="hover:text-amber-200 transition-colors">Find Golfers</Link>
              <Link to="/terms" className="hover:text-amber-200 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-amber-200 transition-colors">Privacy Policy</Link>
              <Link to="/contact" className="hover:text-amber-200 transition-colors">Contact</Link>
              <Link to="/donate" className="hover:text-amber-200 transition-colors text-amber-400">☕ Donate</Link>
              {user && (
                <button
                  onClick={() => setShowReport(true)}
                  className="hover:text-amber-200 transition-colors text-green-400"
                >
                  Report Issue
                </button>
              )}
            </div>
          </div>
          <p className="text-center text-green-600 text-xs">
            GolfBoard Scores is a free, community-driven platform. Scores are self-reported and honor-based.
          </p>
        </div>
      </footer>
      {showReport && <ReportIssueModal onClose={() => setShowReport(false)} />}
    </>
  );
}